<template>
  <div class="filters-section shadow rounded-lg mt-3">
    <div class="rounded-lg shadow-sm border-0 mb-3 filter-card">
      <div class="card-body p-4">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <p class="mb-0 fw-bold text-dark d-flex align-items-center text-x">
            <i class="fa-solid fa-filter me-1 filter-icon fs-8"></i>
            {{ title }}
          </p>

          <!-- right slot (e.g., a reset all button, extra actions) -->
          <slot name="header-actions" />
        </div>

        <!-- Search -->
        <div v-if="show_search" class="mb-2">
          <div class="search-wrapper">
            <input
              :id="`${id_prefix}_search_input`"
              v-model="local_filters[search_key]"
              type="text"
              class="form-control form-control-sm search-input text-xxs rounded-lg"
              :placeholder="search_placeholder"
              @input="emit_change"
            />
            <button
              v-if="local_filters[search_key]"
              class="btn btn-clear btn  btn-sm rounded-small text-xxs text-dark"
              @click="clear_search"
              aria-label="Clear search"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Core filter fields -->
        <div class="row g-3">
          <!-- Entity select -->
          <div v-if="show_entity" class="col-md-6 col-lg-4">
            <label class="form-label text-xs fw-semibold text-dark mb-2">
              <i class="fa-solid fa-building me-1 text-muted"></i>
              {{ entity_label }}
            </label>
            <select
              :id="`${id_prefix}_entity_select`"
              v-model="local_filters[entity_key]"
              class="form-select form-select-sm text-xs rounded-small p-2 shadow-sm"
              @change="emit_change"
            >
              <option :value="''">{{ entity_all_label }}</option>
              <option
                v-for="opt in entity_options"
                :key="opt[entity_value_key]"
                :value="String(opt[entity_value_key])"
              >
                {{ opt[entity_label_key] }}
              </option>
            </select>
          </div>

          <!-- From date -->
          <div v-if="show_dates" class="col-md-6 col-lg-4">
            <label class="form-label text-xs fw-semibold text-dark mb-2">
              <i class="fa-solid fa-calendar me-1 text-muted"></i>
              {{ from_label }}
            </label>
            <input
              :id="`${id_prefix}_from_date`"
              v-model="local_filters[date_from_key]"
              type="date"
              class="form-control form-control-sm text-xs rounded-small p-2 shadow-sm"
              @change="emit_change"
            />
          </div>

          <!-- To date -->
          <div v-if="show_dates" class="col-md-6 col-lg-4">
            <label class="form-label text-xs fw-semibold text-dark mb-2">
              <i class="fa-solid fa-calendar me-1 text-muted"></i>
              {{ to_label }}
            </label>
            <input
              :id="`${id_prefix}_to_date`"
              v-model="local_filters[date_to_key]"
              type="date"
              class="form-control form-control-sm text-xxs rounded-small p-2 shadow-sm"
              @change="emit_change"
            />
          </div>

          <!-- Extra area (drop in any custom filter controls) -->
          <slot name="extra" :filters="local_filters" :emit-change="emit_change" />
        </div>

        <!-- Active chips -->
        <div v-if="has_active" class="active-filters mt-4 pt-3">
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <span class="text-xs fw-semibold text-muted me-1">Active :</span>

            <span v-if="local_filters[search_key]" class="filter-chip text-xs">
              <i class="fa-solid fa-search me-1"></i>
              "{{ truncated_search }}"
              <i class="fa-solid fa-times ms-2 chip-close" @click="clear_search"></i>
            </span>

            <span v-if="local_filters[entity_key]" class="filter-chip text-xs">
              <i class="fa-solid fa-building me-1"></i>
              {{ resolved_entity_label }}
              <i class="fa-solid fa-times ms-2 chip-close" @click="clear_entity"></i>
            </span>

            <span v-if="local_filters[date_from_key]" class="filter-chip text-xs">
              <i class="fa-solid fa-calendar me-1"></i>
              {{ from_label }}: {{ format_date(local_filters[date_from_key]) }}
              <i class="fa-solid fa-times ms-2 chip-close" @click="clear_from"></i>
            </span>

            <span v-if="local_filters[date_to_key]" class="filter-chip text-xs">
              <i class="fa-solid fa-calendar me-1"></i>
              {{ to_label }}: {{ format_date(local_filters[date_to_key]) }}
              <i class="fa-solid fa-times ms-2 chip-close" @click="clear_to"></i>
            </span>

            <button
              class="btn btn-link btn-sm btn-primary-dark rounded-small text-xxs text-dark text-decoration-none p-1 ms-2 text-xs"
              @click="reset_all"
            >
              <i class="fa-solid fa-times-circle me-1"></i>
              Clear all
            </button>
          </div>
        </div>

        <!-- Footer (use slot if you want custom counts/text) -->
        <div class="filter-footer mt-4 pt-3">
          <div class="d-flex justify-content-between align-items-center">
            <slot name="footer">
              <div class="results-count" v-if="results_text">
                <i class="fa-solid fa-list me-2 text-muted text-xxs"></i>
                <span class="fw-semibold text-dark text-xxs">{{ results_text }}</span>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * ReusableFilters.vue
 * A drop-in, configurable filter bar:
 * - Works with v-model to sync a filters object
 * - Emits 'update:modelValue' and 'filters-changed' on change
 * - Toggle fields: search, entity select, dates
 * - Map keys to your API params via props (snake_case friendly)
 */
export default {
  name: "ReusableFilters",

  props: {
    /* v-model binding for filters */
    modelValue: {
      type: Object,
      default: () => ({}),
    },

    /* toggles */
    show_search: { type: Boolean, default: true },
    show_entity: { type: Boolean, default: true },
    show_dates: { type: Boolean, default: true },

    /* field labels/placeholders */
    title: { type: String, default: "Filters" },
    search_placeholder: {
      type: String,
      default: "Search by name or type...",
    },
    entity_label: { type: String, default: "Entity Type" },
    entity_all_label: { type: String, default: "All Types" },
    from_label: { type: String, default: "From Date" },
    to_label: { type: String, default: "To Date" },

    /* keys for the filters object (lets you reuse everywhere) */
    search_key: { type: String, default: "search" },
    entity_key: { type: String, default: "entity_type_id" },
    date_from_key: { type: String, default: "date_from" },
    date_to_key: { type: String, default: "date_to" },

    /* entity options + mapping */
    entity_options: { type: Array, default: () => [] },
    entity_value_key: { type: String, default: "id" },
    entity_label_key: { type: String, default: "name" },

    /* footer summary text (optional) */
    results_text: { type: String, default: "" },

    /* prefix so ids/classes don't clash when used multiple times on a page */
    id_prefix: { type: String, default: "rf" },
  },

  data() {
    return {
      local_filters: this._with_defaults(this.modelValue),
    };
  },

  computed: {
    has_active() {
      return Boolean(
        (this.show_search && this.local_filters[this.search_key]) ||
          (this.show_entity && this.local_filters[this.entity_key]) ||
          (this.show_dates &&
            (this.local_filters[this.date_from_key] ||
              this.local_filters[this.date_to_key]))
      );
    },
    truncated_search() {
      const v = this.local_filters[this.search_key] || "";
      return v.length > 20 ? `${v.substring(0, 20)}...` : v;
    },
    resolved_entity_label() {
      const val = this.local_filters[this.entity_key];
      if (!val) return "";
      const found = this.entity_options.find(
        (o) => String(o[this.entity_value_key]) === String(val)
      );
      return found ? found[this.entity_label_key] : String(val);
    },
  },

  watch: {
    modelValue: {
      deep: true,
      handler(next) {
        // keep local in sync if parent updates v-model
        this.local_filters = this._with_defaults(next);
      },
    },
  },

  methods: {
    _with_defaults(obj) {
      // ensure keys exist so v-model stays reactive
      return {
        [this.search_key]: obj?.[this.search_key] ?? "",
        [this.entity_key]: obj?.[this.entity_key] ?? "",
        [this.date_from_key]: obj?.[this.date_from_key] ?? "",
        [this.date_to_key]: obj?.[this.date_to_key] ?? "",
        // keep any extra custom keys intact
        ...obj,
      };
    },

    emit_change() {
      const copy = { ...this.local_filters };
      this.$emit("update:modelValue", copy);
      this.$emit("filters-changed", copy);
    },

    reset_all() {
      this.local_filters[this.search_key] = "";
      this.local_filters[this.entity_key] = "";
      this.local_filters[this.date_from_key] = "";
      this.local_filters[this.date_to_key] = "";
      this.emit_change();
    },

    clear_search() {
      this.local_filters[this.search_key] = "";
      this.emit_change();
    },

    clear_entity() {
      this.local_filters[this.entity_key] = "";
      this.emit_change();
    },

    clear_from() {
      this.local_filters[this.date_from_key] = "";
      this.emit_change();
    },

    clear_to() {
      this.local_filters[this.date_to_key] = "";
      this.emit_change();
    },

    format_date(date_str) {
      if (!date_str) return "";
      const d = new Date(date_str);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>

/* Search Input Styling */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.btn-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  transition: all 0.2s ease;
  z-index: 1;
}
.btn-clear:hover {
  background: #f8f9fa;
}

/* Filter Select Inputs */
.filter-select {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  transition: all 0.2s ease;
  height: 42px;
}
.filter-select:focus {
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.1);
  outline: none;
}

/* Active Filters Section */
.active-filters {
  border-top: 1px solid #e9ecef;
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 20px;
  font-weight: 500;
  transition: all 0.2s ease;
  animation: fadeIn 0.3s ease;
}
.filter-chip i.fa-solid:not(.chip-close) {
  opacity: 0.7;
}
.chip-close {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}
.chip-close:hover {
  opacity: 1;
}

/* Footer Styling */
.filter-footer {
  border-top: 1px solid #e9ecef;
}

/* Responsive */
@media (max-width: 768px) {
  .search-input { font-size: 0.875rem; }
  .filter-chip { font-size: 0.8rem; padding: 0.35rem 0.65rem; }
  .card-body { padding: 1.25rem !important; }
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>